import { create } from 'zustand'
import { User, UserRole, LoginDto, ValidateCodeDto } from '@/types'
import { authService } from '@/services/auth.service'
import { usersService } from '@/services/users.service'
import { authStorage } from '@/utils/storage'
import { isTokenExpired } from '@/utils/jwt'
import {
  isImpersonating,
  prepareImpersonationSession,
  resolveAuthToken,
  stripImpersonationTokenFromUrl,
} from '@/utils/impersonation'
import { hasAnyRole, isNeoPlatformRole } from '@/utils/role-access'
import { applyBrandingFromUser, initializeBranding } from '@/utils/branding'
import { useBrandingStore } from '@/stores/branding.store'
import { resetSessionStores } from '@/stores/reset-session-stores'

function buildAuthFlags(user: User) {
  return {
    user,
    isAdmin: user.role === UserRole.ADMIN,
    isViewer: isNeoPlatformRole(user.role),
    isReseller: user.role === UserRole.RESELLER,
    isWholesaler: user.role === UserRole.WHOLESALER,
    isNeoPlatform: isNeoPlatformRole(user.role),
  }
}

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isViewer: boolean
  isReseller: boolean
  isWholesaler: boolean
  isNeoPlatform: boolean
  isImpersonating: boolean
  initializeAuth: () => Promise<void>
  login: (credentials: LoginDto) => Promise<{ code?: number }>
  validateCode: (data: ValidateCodeDto) => Promise<void>
  logout: () => void
  setCurrentUser: (user: User) => void
  refreshUser: () => Promise<void>
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
}

const emptyAuthFlags = {
  user: null,
  isAdmin: false,
  isViewer: false,
  isReseller: false,
  isWholesaler: false,
  isNeoPlatform: false,
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...emptyAuthFlags,
  isLoading: true,
  isAuthenticated: false,
  isImpersonating: false,

  initializeAuth: async () => {
    prepareImpersonationSession()

    try {
      const token = resolveAuthToken()
      const impersonating = isImpersonating()

      if (token && !isTokenExpired(token)) {
        const currentUser = await usersService.getCurrent()
        set({
          isAuthenticated: true,
          isImpersonating: impersonating,
          ...buildAuthFlags(currentUser),
        })
        if (!impersonating) {
          authStorage.setUser(get().user!)
        }
        useBrandingStore.getState().setBranding(applyBrandingFromUser(currentUser))
      } else if (!impersonating) {
        authStorage.clear()
        set({ isAuthenticated: false, isImpersonating: false, ...emptyAuthFlags })
      } else {
        stripImpersonationTokenFromUrl()
        set({ isImpersonating: false })
      }
    } catch {
      if (isImpersonating()) {
        stripImpersonationTokenFromUrl()
        set({ isImpersonating: false })

        const adminToken = authStorage.getToken()
        if (adminToken && !isTokenExpired(adminToken)) {
          try {
            const currentUser = await usersService.getCurrent()
            set({
              isAuthenticated: true,
              isImpersonating: false,
              ...buildAuthFlags(currentUser),
            })
            authStorage.setUser(get().user!)
            useBrandingStore.getState().setBranding(applyBrandingFromUser(currentUser))
            return
          } catch {
            authStorage.clear()
          }
        }
      } else {
        authStorage.clear()
      }
    } finally {
      set({ isLoading: false })
    }
  },

  login: async (credentials: LoginDto) => {
    const response = await authService.login(credentials)
    return response
  },

  validateCode: async (data: ValidateCodeDto) => {
    resetSessionStores()
    const response = await authService.validateCode(data)
    authStorage.setToken(response.access_token)

    try {
      const currentUser = await usersService.getCurrent()
      set({
        isAuthenticated: true,
        isImpersonating: false,
        ...buildAuthFlags(currentUser),
      })
      authStorage.setUser(get().user!)
      useBrandingStore.getState().setBranding(applyBrandingFromUser(currentUser))
    } catch (error) {
      authStorage.clear()
      throw error
    }
  },

  logout: () => {
    if (isImpersonating()) {
      stripImpersonationTokenFromUrl()
      resetSessionStores()
      set({
        isImpersonating: false,
        isLoading: true,
      })
      void get().initializeAuth()
      return
    }

    authStorage.clear()
    resetSessionStores()
    set({
      isAuthenticated: false,
      isImpersonating: false,
      ...emptyAuthFlags,
    })
    useBrandingStore.getState().setBranding(initializeBranding())
  },

  setCurrentUser: (user: User) => {
    set(buildAuthFlags(user))
    if (!isImpersonating()) {
      authStorage.setUser(user)
    }
    useBrandingStore.getState().setBranding(applyBrandingFromUser(user))
  },

  refreshUser: async () => {
    try {
      const currentUser = await usersService.getCurrent()
      set({
        ...buildAuthFlags(currentUser),
        isImpersonating: isImpersonating(),
      })
      if (!isImpersonating()) {
        authStorage.setUser(get().user!)
      }
      useBrandingStore.getState().setBranding(applyBrandingFromUser(currentUser))
    } catch {
      get().logout()
    }
  },

  hasRole: (role: UserRole) => {
    return get().user?.role === role
  },

  hasAnyRole: (roles: UserRole[]) => {
    return hasAnyRole(get().user, roles)
  },
}))
